/**
 * Project Name: Brainsie eCommerce v4
 * Component: Tab Layout
 * Description: Interactive tab layout with image and content sections
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React, { useState } from "react";
import { Box, Container, Typography, Tabs, Tab, Grid } from "@mui/material";
import ComponentRenderer from "../ComponentRenderer";

export default function TabLayout({
  section,
  components,
  loading,
  error,
  pageData,
  darkMode,
  theme,
}) {
  // Get section settings
  const sectionSettings = section.Section_Settings
    ? JSON.parse(section.Section_Settings)
    : {};

  // State for active tab
  const [activeTab, setActiveTab] = useState(0);

  // Filter and sort tab components by Component_Id
  const tabComponents =
    components
      ?.filter((component) => component.Component_Name === "Simple Tab")
      ?.sort((a, b) => a.Component_Id - b.Component_Id) || [];

  // Helper function to get theme-aware text color
  const getTextColor = () => {
    if (sectionSettings.textColor) {
      return sectionSettings.textColor;
    }
    return darkMode ? theme.palette.common.white : theme.palette.text.primary;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  console.log("🟡 TAB LAYOUT RENDERING - Components:", tabComponents.length);

  return (
    <Box
      component="section"
      className="tab-layout"
      sx={{
        py: sectionSettings.paddingY || 8,
        px: sectionSettings.paddingX || 0,
        backgroundColor: "transparent",
        color: getTextColor(),
        ...sectionSettings.customStyles,
      }}
    >
      <Container maxWidth={sectionSettings.maxWidth || "lg"}>
        {/* Section Header */}
        {section.Section_Heading && section.Section_Heading !== "NA" && (
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                mb: 2,
                color: sectionSettings.headingColor || getTextColor(),
                fontWeight: 600,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                lineHeight: 1.2,
              }}
            >
              {section.Section_Heading}
            </Typography>

            {section.Section_Sub_Text && section.Section_Sub_Text !== "NA" && (
              <Typography
                variant="body1"
                sx={{
                  color: sectionSettings.bodyColor || getTextColor(),
                  opacity: 0.8,
                  lineHeight: 1.6,
                  fontSize: "1.1rem",
                  maxWidth: "600px",
                  mx: "auto",
                }}
              >
                {section.Section_Sub_Text}
              </Typography>
            )}
          </Box>
        )}

        {loading ? (
          <Box sx={{ textAlign: "center", color: getTextColor() }}>
            Loading components...
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", color: theme.palette.error.main }}>
            Error loading components: {error.message}
          </Box>
        ) : tabComponents.length > 0 ? (
          <Box>
            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 4 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    color: darkMode
                      ? theme.palette.grey[400]
                      : theme.palette.text.secondary,
                    fontWeight: 500,
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    textTransform: "none",
                    minWidth: { xs: 120, md: 160 },
                    "&.Mui-selected": {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: theme.palette.primary.main,
                    height: 3,
                  },
                }}
              >
                {tabComponents.map((component, index) => (
                  <Tab
                    key={component.Component_Id}
                    label={component.Primary_Text || `Tab ${index + 1}`}
                    id={`tab-${index}`}
                    aria-controls={`tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Tab Content */}
            {tabComponents.map((component, index) => (
              <Box
                key={component.Component_Id}
                role="tabpanel"
                hidden={activeTab !== index}
                id={`tabpanel-${index}`}
                aria-labelledby={`tab-${index}`}
                sx={{
                  display: activeTab === index ? "block" : "none",
                }}
              >
                {activeTab === index && (
                  <ComponentRenderer
                    component={component}
                    section={section}
                    darkMode={darkMode}
                    theme={theme}
                    isActiveTab={true}
                  />
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", color: getTextColor() }}>
            No tab components found.
          </Box>
        )}

        {/* Render any other components not specifically handled */}
        {components
          ?.filter((component) => component.Component_Name !== "Simple Tab")
          ?.map((component) => (
            <Box key={component.Component_Id} sx={{ mt: 4 }}>
              <ComponentRenderer
                component={component}
                section={section}
                darkMode={darkMode}
                theme={theme}
              />
            </Box>
          ))}
      </Container>
    </Box>
  );
}
